import React, {useEffect, useMemo, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {IconTemplate, IconX, IconChevronDown, IconLoader2} from '@tabler/icons-react';
import styles from './CreateTemplatePopup.module.scss';
import {usePostTemplate} from '../../api/template-api/template-api';
import {useProjects} from '../../hooks/useProjects';
import {useCurrentUserPermissions} from '../../hooks/useCurrentUserPermissions';
import {useToast} from '../ui/Toast/ToastProvider';
import type {TemplateDTO} from '../../api/model';
import type {ProjectDto} from '../../api/model';

interface ProjectSummary {
    id: string;
    title: string;
    description?: string;
}

interface CreateTemplatePopupProps {
    onClose: () => void;
    onSuccess?: () => void;
    initialProject?: ProjectSummary;
}

const CreateTemplatePopup: React.FC<CreateTemplatePopupProps> = ({onClose, onSuccess, initialProject}) => {
    const {t} = useTranslation('templates');
    const {showSuccess, showError} = useToast();
    const queryClient = useQueryClient();
    const {isSystemAdmin} = useCurrentUserPermissions();
    const {data: projects = [], isLoading} = useProjects();
    const [selectedProjectId, setSelectedProjectId] = useState(initialProject?.id ?? '');
    const [templateName, setTemplateName] = useState(initialProject ? `${initialProject.title} Vorlage` : '');
    const [templateDescription, setTemplateDescription] = useState(initialProject?.description ?? '');

    const selectedProject = useMemo(() => {
        if (initialProject && initialProject.id === selectedProjectId) return initialProject;
        return projects
            .filter((project) => project.id != null)
            .map((project) => ({
                id: String(project.id),
                title: project.name || 'Untitled Project',
                description: project.description || '',
            }))
            .find((project) => project.id === selectedProjectId);
    }, [initialProject, projects, selectedProjectId]);

    const mutation = usePostTemplate({
        mutation: {
            onSuccess: (template) => {
                queryClient.setQueryData<TemplateDTO[]>(['/template'], (current = []) => {
                    const next = current.filter((entry) => entry.id !== template.id);
                    return [template, ...next];
                });
                showSuccess(
                    t('templates.create.successTitle', 'Template created'),
                    t('templates.create.successMessage', {name: template.name || template.id || ''})
                );
                queryClient.invalidateQueries({queryKey: ['/template']});
                onSuccess?.();
                onClose();
            },
            onError: (error: any) => {
                showError(
                    t('templates.create.errorTitle', 'Template creation failed'),
                    error?.message || t('templates.create.errorMessage', 'Could not save the template.')
                );
            },
        },
    }, queryClient);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    useEffect(() => {
        if (!initialProject) return;
        setSelectedProjectId(initialProject.id);
        setTemplateName(`${initialProject.title} Vorlage`);
        setTemplateDescription(initialProject.description ?? '');
    }, [initialProject]);

    useEffect(() => {
        if (initialProject || !selectedProject) return;
        if (!templateName.trim()) {
            setTemplateName(`${selectedProject.title} Vorlage`);
        }
        if (!templateDescription.trim()) {
            setTemplateDescription(selectedProject.description ?? '');
        }
    }, [initialProject, selectedProject, templateDescription, templateName]);

    const canSubmit = !isSystemAdmin && !mutation.isPending && !!selectedProjectId && !!templateName.trim();

    const handleSubmit = () => {
        const numericProjectId = Number(selectedProjectId);
        if (!Number.isFinite(numericProjectId) || numericProjectId <= 0) {
            showError(t('templates.create.invalidProjectTitle', 'Invalid project'), t('templates.create.invalidProjectMessage', 'Please select a valid project.'));
            return;
        }

        mutation.mutate({
            data: {
                projectId: numericProjectId,
                name: templateName.trim(),
                description: templateDescription.trim() || undefined,
            },
        });
    };

    const handleOverlayClick = (event: React.MouseEvent) => {
        if (event.target === event.currentTarget) onClose();
    };

    if (isSystemAdmin) {
        return (
            <div className={styles.overlay} onClick={handleOverlayClick}>
                <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.header}>
                        <div className={styles.headerTop}>
                            <h2 className={styles.title}>{t('templates.create.title', 'Save project as template')}</h2>
                            <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                                <IconX size={20}/>
                            </button>
                        </div>
                    </div>
                    <div className={styles.content}>
                        <p className={styles.blockedText}>
                            {t('templates.create.systemAdminBlocked', 'System admin accounts are not associated with a tenant and cannot create templates.')}
                        </p>
                    </div>
                    <div className={styles.footer}>
                        <button className={styles.cancelButton} onClick={onClose}>
                            {t('templates.create.cancel', 'Cancel')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.overlay} onClick={handleOverlayClick}>
            <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <div className={styles.headerTop}>
                        <div>
                            <h2 className={styles.title}>{t('templates.create.title', 'Save project as template')}</h2>
                            <p className={styles.description}>{t('templates.create.description', 'Choose a project, name the template and save it outside the project context.')}</p>
                        </div>
                        <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                            <IconX size={20}/>
                        </button>
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('templates.create.fields.project', 'Source project')}</label>
                        <div className={styles.selectWrapper}>
                            {isLoading ? (
                                <div className={styles.loadingContainer}>
                                    <IconLoader2 className={styles.loadingIcon}/>
                                    <span>{t('templates.create.loadingProjects', 'Loading projects…')}</span>
                                </div>
                            ) : (
                                <select
                                    value={selectedProjectId}
                                    onChange={(event) => setSelectedProjectId(event.target.value)}
                                    className={`${styles.input} ${selectedProjectId ? styles.hasContent : ''}`}
                                    disabled={!!initialProject}
                                >
                                    <option
                                        value="">{t('templates.create.fields.projectSelect', 'Select a project')}</option>
                                    {(projects ?? [])
                                        .filter((project): project is ProjectDto & { id: number } => project.id != null)
                                        .map((project) => (
                                            <option key={project.id} value={String(project.id)}>
                                                {project.name || `Project #${project.id}`}
                                            </option>
                                        ))}
                                </select>
                            )}
                            <div className={styles.iconWrapper}>
                                <IconChevronDown color="#525866"/>
                            </div>
                        </div>
                    </div>

                    {selectedProject && (
                        <div className={styles.projectPreview}>
                            <div className={styles.projectPreviewHeader}>
                                <span className={styles.projectPreviewTitle}>{selectedProject.title}</span>
                                {selectedProject.description && (
                                    <span className={styles.projectPreviewText}>{selectedProject.description}</span>
                                )}
                            </div>
                        </div>
                    )}

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('templates.create.fields.name', 'Template name')}</label>
                        <input
                            type="text"
                            value={templateName}
                            onChange={(event) => setTemplateName(event.target.value)}
                            className={`${styles.input} ${templateName ? styles.hasContent : ''}`}
                            placeholder={t('templates.create.fields.namePlaceholder', 'Enter a template name')}
                            maxLength={80}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label
                            className={styles.label}>{t('templates.create.fields.description', 'Description')}</label>
                        <textarea
                            value={templateDescription}
                            onChange={(event) => setTemplateDescription(event.target.value)}
                            className={`${styles.input} ${styles.textarea} ${templateDescription ? styles.hasContent : ''}`}
                            placeholder={t('templates.create.fields.descriptionPlaceholder', 'Describe what this template is for')}
                            rows={3}
                            maxLength={250}
                        />
                    </div>
                </div>

                <div className={styles.footer}>
                    <button className={styles.cancelButton} onClick={onClose}>
                        {t('templates.create.cancel', 'Cancel')}
                    </button>
                    <button className={styles.submitButton} onClick={handleSubmit} disabled={!canSubmit}>
                        <IconTemplate size={18}/>
                        <span>{mutation.isPending ? t('templates.create.saving', 'Saving…') : t('templates.create.submit', 'Save template')}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateTemplatePopup;
